import { Grid, Slider, styled } from '@mui/material';
import { GivelifyButton } from '../button';
import { GivelifyModal } from '../modal';
import { GivelifyNotification } from '../notifications';
import { DesignTokens } from '../specify';
import { GivelifyTooltip } from '../tooltip';
import { AVATAR_CIRCLE_INSET } from './constants';

const avatarCircleInset = `${AVATAR_CIRCLE_INSET}px`;

export const StyledModal = styled(GivelifyModal, {
    shouldForwardProp: (prop) => prop !== 'isLoading' && prop !== 'isHidden',
})<{ isLoading?: boolean; isHidden?: boolean }>(
    ({ theme, isLoading, isHidden }) => ({
        visibility: isHidden ? 'hidden' : 'visible',
        '& .MuiPaper-root': {
            width: 'min(100%, 540px)',
            maxWidth: 'calc(100% - 32px)',
            height: 'auto',
            background: '#fff',
            overflow: 'hidden',
            overflowY: 'auto',
            padding: '0',
        },

        '& .modal-header': {
            padding: '24px 24px 0 24px',
        },

        '& .modal-title': {
            padding: '0 24px',
            display: '-webkit-box',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
            width: '100%',
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
        },

        '& .modal-actions': {
            padding: '8px 24px 48px 24px',
            [theme.breakpoints.down('mobile')]: {
                padding: '0 24px 48px 24px',

                '& .reset-button': {
                    marginBottom: '16px',
                },
            },
        },

        '& .loading-container': {
            padding: isLoading ? '18px 24px 0 24px' : 0,
        },
    }),
);

export const Wrapper = styled(Grid, {
    shouldForwardProp: (prop) => prop !== 'isUploading',
})<{ isUploading?: boolean }>(({ theme, isUploading }) => ({
    background: isUploading ? DesignTokens.color.backgroundSecondary : '#fff',
}));

export const CropWrapper = styled('div')(({ theme }) => ({
    position: 'relative',
    width: '100%',
    padding: '24px',
    border: '1px solid transparent',
    display: 'block',
    background: DesignTokens.color.backgroundComponentSecondary,
    [theme.breakpoints.down('mobile')]: {
        width: '100%',
    },
}));

export const CropContainer = styled('div', {
    shouldForwardProp: (prop) => prop !== 'avatar' && prop !== 'aspectRatio',
})<{ avatar?: boolean; aspectRatio?: number }>(
    ({ theme, avatar, aspectRatio }) => ({
        position: 'relative',
        width: '100%',
        height: 'auto',
        aspectRatio: String(aspectRatio || 16 / 9),
        minHeight: avatar ? '260px' : '200px',
        overflow: 'hidden',
        borderRadius: avatar ? '24px' : '18px',
        border: `1px solid ${DesignTokens.color.dividerPrimaryLight}`,
        boxShadow: '0 1px 0 rgba(0, 0, 0, 0.04)',
        cursor: 'move',
        backgroundColor: DesignTokens.color.backgroundSecondary,
        touchAction: 'none',
        [theme.breakpoints.down('mobile')]: {
            height: 'auto',
            minHeight: avatar ? '220px' : '180px',
        },
    }),
);

export const EditorBackground = styled('div')({
    position: 'absolute',
    inset: 0,
    overflow: 'hidden',
    zIndex: 0,
});

export const Background = styled('div', {
    shouldForwardProp: (prop) => prop !== 'isBlur',
})<{ isBlur?: boolean }>(({ isBlur }) => ({
    position: 'absolute',
    inset: 0,
    ...(isBlur && {
        inset: '-0%',
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        filter: 'blur(8px)',
        transform: 'scale(1.05)',
    }),
}));

const ThirdsGridBackground = `linear-gradient(
                to right,
                transparent 33.333%,
                rgba(255, 255, 255, 0.7) 33.333%,
                rgba(255, 255, 255, 0.7) calc(33.333% + 1px),
                transparent calc(33.333% + 1px),
                transparent 66.666%,
                rgba(255, 255, 255, 0.7) 66.666%,
                rgba(255, 255, 255, 0.7) calc(66.666% + 1px),
                transparent calc(66.666% + 1px)
            ),
            linear-gradient(
                to bottom,
                transparent 33.333%,
                rgba(255, 255, 255, 0.7) 33.333%,
                rgba(255, 255, 255, 0.7) calc(33.333% + 1px),
                transparent calc(33.333% + 1px),
                transparent 66.666%,
                rgba(255, 255, 255, 0.7) 66.666%,
                rgba(255, 255, 255, 0.7) calc(66.666% + 1px),
                transparent calc(66.666% + 1px)
            )`;

export const ThirdsGrid = styled('div', {
    shouldForwardProp: (prop) => prop !== 'avatar',
})<{ avatar?: boolean }>({
    position: 'absolute',
    inset: 0,
    zIndex: 3,
    pointerEvents: 'none',
    opacity: 0.6,
    '&::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        backgroundImage: ThirdsGridBackground,
    },
});

export const AvatarThirdsGrid = styled('div')({
    position: 'absolute',
    top: '50%',
    left: '50%',
    height: `calc(100% - (${avatarCircleInset} * 2))`,
    aspectRatio: '1 / 1',
    transform: 'translate(-50%, -50%)',
    borderRadius: '50%',
    overflow: 'hidden',
    zIndex: 3,
    pointerEvents: 'none',
    opacity: 0.6,
    '&::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        backgroundImage: ThirdsGridBackground,
    },
});

export const AvatarOverlay = styled('div')({
    position: 'absolute',
    top: '50%',
    left: '50%',
    height: `calc(100% - (${avatarCircleInset} * 2))`,
    aspectRatio: '1 / 1',
    transform: 'translate(-50%, -50%)',
    borderRadius: '50%',
    zIndex: 2,
    pointerEvents: 'none',
    boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
});

export const EditorImage = styled('img', {
    shouldForwardProp: (prop) => prop !== 'isRotating',
})<{ isRotating?: boolean }>(({ isRotating }) => ({
    position: 'absolute',
    top: '50%',
    left: '50%',
    userSelect: 'none',
    pointerEvents: 'none',
    willChange: 'transform',
    zIndex: 1,
    ...(isRotating && {
        transition: 'transform 220ms ease',
    }),
}));

export const Controls = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: '18px',
    paddingTop: '24px',
    marginBottom: '8px',
});

export const UploadProgress = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    paddingTop: '36px',
    paddingBottom: '23px',
}));

export const UploadProgressBar = styled('div')({
    width: '100%',
    height: '4px',
    borderRadius: '4px',
    backgroundColor: DesignTokens.color.backgroundComponentQuaternary,
    overflow: 'hidden',
});

export const UploadProgressFill = styled('div')<{ progress: number }>(
    ({ progress }) => ({
        width: `${progress}%`,
        height: '100%',
        borderRadius: '4px',
        backgroundColor: DesignTokens.color.backgroundComponentPrimaryAccent,
        transition: 'width 200ms ease',
    }),
);

export const UploadProgressRow = styled('div')({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
});

export const ControlsRow = styled('div', {
    shouldForwardProp: (prop) => prop !== 'hideBackgroundSelection',
})<{ hideBackgroundSelection?: boolean }>(
    ({ theme, hideBackgroundSelection = false }) => ({
        display: 'grid',
        gridTemplateColumns: hideBackgroundSelection
            ? '1fr auto'
            : '1fr auto auto',
        gap: '24px',
        alignItems: 'end',
        [theme.breakpoints.down('mobile')]: {
            gridTemplateColumns: '1fr',
            gap: '16px',
            alignItems: 'stretch',
        },
        '& .control-label': {
            [theme.breakpoints.down('mobile')]: {
                fontSize: '12px',
            },
        },
    }),
);

export const ZoomControls = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    minWidth: 0,
    [theme.breakpoints.down('mobile')]: {
        display: 'grid',
        gridTemplateColumns: '72px 1fr',
        alignItems: 'center',
        columnGap: '16px',
        rowGap: '0',
        '& > *:first-of-type': {
            marginBottom: 0,
        },
        '& > *:last-of-type': {
            width: '100%',
        },
    },
}));

export const ZoomRow = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    [theme.breakpoints.down('mobile')]: {
        gap: '10px',
    },
}));

export const IconButton = styled(GivelifyButton)(({ theme }) => ({
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    border: `1px solid ${DesignTokens.color.dividerPrimaryLight}`,
    background: '#fff',
    color: DesignTokens.color.textAccentDefault,
    fontSize: '24px',
    lineHeight: 1,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    minWidth: 0,
    padding: 0,
}));

export const RotateControls = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    alignItems: 'center',
    [theme.breakpoints.down('mobile')]: {
        display: 'grid',
        gridTemplateColumns: '72px 1fr',
        alignItems: 'center',
        columnGap: '16px',
        rowGap: '0',
        alignSelf: 'flex-start',
        '& > *:last-of-type': {
            justifySelf: 'start',
        },
    },
}));

export const BackgroundControls = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    alignItems: 'flex-start',
    [theme.breakpoints.down('mobile')]: {
        display: 'grid',
        gridTemplateColumns: '72px 1fr',
        alignItems: 'center',
        columnGap: '16px',
        rowGap: '0',
        '& > *:last-of-type': {
            width: '100%',
        },
    },
}));

export const BackgroundOptions = styled('div')({
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
});

export const BackgroundOption = styled(GivelifyButton, {
    shouldForwardProp: (prop) => prop !== 'isSelected' && prop !== 'isDisabled',
})<{
    isSelected?: boolean;
    isDisabled?: boolean;
}>(({ isSelected, isDisabled }) => ({
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    border: `1px solid ${DesignTokens.color.dividerPrimaryLight}`,
    padding: '1px',
    background: 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'border-color 150ms ease, opacity 150ms ease',
    cursor: 'pointer',
    minWidth: 0,
    ...(isSelected && {
        borderColor: DesignTokens.color.textAccentDefault,
    }),
    ...(isDisabled && {
        opacity: 0.4,
    }),
    overflow: 'hidden',
}));

export const BackgroundOptionContainer = styled('div')(({ theme }) => ({
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    borderRadius: '6px',
}));

export const OptionBlur = styled('div')({
    width: '100%',
    height: '100%',
    borderRadius: '6px',
    backgroundPosition: 'center',
    backgroundSize: 'cover',
    filter: 'blur(6px)',
});

export const OptionColor = styled('div')({
    width: '100%',
    height: '100%',
    borderRadius: '6px',
});

export const ZoomSlider = styled(Slider)({
    flex: 1,
    '& .MuiSlider-rail': {
        height: '4px',
        borderRadius: '20px',
        backgroundColor: DesignTokens.color.backgroundComponentQuaternary,
        opacity: 1,
    },
    '& .MuiSlider-track': {
        height: '4px',
        borderRadius: '20px',
        backgroundColor: DesignTokens.color.textAccentDefault,
        border: 'none',
    },
    '& .MuiSlider-thumb': {
        width: '20px',
        height: '20px',
        backgroundColor: DesignTokens.color.textAccentDefault,

        '&:hover, &:active': {
            boxShadow: 'none',
        },
    },
});

export const ActionsRow = styled(Grid)(({ theme }) => ({
    flexDirection: 'row',
    '& button': {
        width: '120px',
        [theme.breakpoints.down('mobile')]: {
            fontSize: '16px',
            width: '152px',
            minWidth: '120px',
        },
    },

    [theme.breakpoints.down('mobile')]: {
        alignItems: 'center',
        flexDirection: 'column',
    },
}));

export const LoadingContainer = styled('div')(({ theme }) => ({
    width: '100%',
    height: '274px',
    background: DesignTokens.color.backgroundPrimary,
    borderRadius: '10px',
    border: `1px solid ${DesignTokens.color.dividerPrimaryLight}`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '16px',
}));

export const FileNameContainer = styled('div')(({ theme }) => ({
    display: 'flex',
    '& .file-name': {
        display: 'inline-block',

        '&.truncate': {
            maxWidth: '300px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',

            [theme.breakpoints.down('mobile')]: {
                maxWidth: '204px',
            },
        },
    },
}));

export const Divider = styled('div', {
    shouldForwardProp: (prop) => prop !== 'top' && prop !== 'bottom',
})<{ top?: boolean; bottom?: boolean }>(({ theme, top, bottom }) => ({
    width: '100%',
    borderTop: `1px solid ${DesignTokens.color.dividerPrimaryLight}`,
    marginBottom: top ? 0 : '24px',
    marginTop: bottom ? 0 : '16px',
}));

export const StyledNotification = styled(GivelifyNotification)(({ theme }) => ({
    '&.MuiSnackbar-root': {
        position: 'absolute',
        height: 'fit-content',
        width: 'calc(100% - 80px)',
        marginLeft: '40px',
        top: '40px',
        left: 0,
        zIndex: 4,
        padding: '16px',

        [theme.breakpoints.down('mobile')]: {
            width: 'calc(100% - 64px)',
            marginLeft: '32px',
            top: '32px',
            padding: '12px',
        },
    },
    '& .MuiPaper-root': {
        maxWidth: '100%',
    },
}));

export const ErrorWrapper = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: '8px',

    '& .actions': {
        display: 'flex',
        justifyContent: 'right',
        gap: '16px',
    },

    '& .action': {
        padding: 0,
        color: DesignTokens.color.textSecondary,
        fontSize: '14px',

        '&:hover': {
            color: DesignTokens.color.textPrimary,
        },
    },
}));

export const StyledTooltip = styled(GivelifyTooltip)({
    '& .MuiTooltip-tooltip': {
        fontSize: '13px',
    },
});
